import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaPhone } from 'react-icons/fa';
import { C, STATUT_NOTAIRE_OPTIONS } from '../utils/constants';
import { fmt, fmtUnit, fmtDate } from '../utils/helpers';
import { XBtn, ThSort, DetailPager } from '../shared/index';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';
import InfoTip from './InfoTip';

// unit : respecte le sélecteur d'unité de la section parente (montants uniquement)

/* ── Constantes ─────────────────────────────────────────────────────────────── */
const TABS = [
  { key: 'solde',      label: 'Soldés',       color: '#2e7d32', info: 'Avance ≥ Prix net (reliquat ≤ 0)' },
  { key: 'partiel',   label: 'Partiel',       color: '#1565c0', info: '0 < Avance < Prix net' },
  { key: 'non_verse', label: 'Non versés',    color: '#c62828', info: 'Aucune avance enregistrée (avance = 0)' },
  { key: 'reliquat',  label: 'Avec reliquat', color: '#e65100', info: 'Reliquat > 10 DH' },
];

const STATUT_BADGE_CFG = {
  SIGNE: { label: 'Signé notaire', bg: '#e8f5e9', color: '#1b5e20' },
  EN_COURS: { label: 'Signature en cours', bg: '#fff3e0', color: '#e65100' },
  CHEZ: { label: 'Signé client', bg: '#e3f2fd', color: '#1565c0' },
  NOT_YET: { label: 'Pas encore signé', bg: '#f3e5f5', color: '#6a1b9a' },
};

const PAGE_SIZE = 25;

/* ── Helpers ────────────────────────────────────────────────────────────────── */
function getStatutKey(row) {
  if (row.bienStatut === 'SIGNE NOTAIRE') return 'SIGNE';
  if (row.enCoursSignature) return 'EN_COURS';
  if (row.chezNotaire) return 'CHEZ';
  return 'NOT_YET';
}

function applyStatutFilter(rows, sel) {
  const s = sel && sel.length ? sel : ['CHEZ', 'EN_COURS'];
  return rows
    .filter(r => s.includes('NOT_YET') || r.bienStatut === 'SIGNE NOTAIRE' || !!r.enCoursSignature || !!r.chezNotaire)
    .filter(r => s.includes('CHEZ') || r.bienStatut === 'SIGNE NOTAIRE' || !!r.enCoursSignature || !r.chezNotaire)
    .filter(r => s.includes('EN_COURS') || r.bienStatut === 'SIGNE NOTAIRE' || !r.enCoursSignature)
    .filter(r => s.includes('SIGNE') || r.bienStatut !== 'SIGNE NOTAIRE');
}

function applyTabFilter(rows, tab) {
  if (tab === 'solde')     return rows.filter(r => r.totalAvance >= r.prixNet);
  if (tab === 'partiel')   return rows.filter(r => r.totalAvance > 0 && r.totalAvance < r.prixNet);
  if (tab === 'non_verse') return rows.filter(r => r.totalAvance === 0);
  if (tab === 'reliquat')  return rows.filter(r => r.reliquat > 10);
  return rows;
}

/* ── Sous-composants ────────────────────────────────────────────────────────── */
const StatutBadge = ({ row }) => {
  const k = getStatutKey(row);
  const cfg = STATUT_BADGE_CFG[k];
  return (
    <span style={{ fontSize: '0.63rem', fontWeight: 700, background: cfg.bg, color: cfg.color, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
};

const TelCell = ({ tels }) => {
  if (!tels) return <span style={{ color: '#bbb' }}>—</span>;
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tels.split(' / ').filter(Boolean).map((tel, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '0.72rem' }}>{tel}</span>
          <button onClick={() => navigator.clipboard?.writeText(tel)} title="Copier"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 0, fontSize: '0.65rem', lineHeight: 1 }}>⎘</button>
          <a href={`tel:${tel}`} title="Appeler" style={{ color: C.green, lineHeight: 1 }}><FaPhone size={9} /></a>
        </span>
      ))}
    </span>
  );
};

/* ── Composant principal ────────────────────────────────────────────────────── */
const NotaireSoonSection = ({ rows = [], loading, multiProjet = true, unit }) => {
  const [statutSel, setStatutSel] = useState(['CHEZ', 'EN_COURS']);
  const [notaireFilter, setNotaireFilter] = useState(new Set());
  const [activeTab, setActiveTab] = useState(null);
  const [viewMode, setViewMode] = useState('resume');
  const [sortKey, setSortKey] = useState('dateReservation');
  const [sortDir, setSortDir] = useState(-1);
  const [page, setPage] = useState(1);

  const toggleStatut = (val) => {
    setStatutSel(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
    setPage(1);
  };

  const onSort = (k) => {
    setSortKey(prev => { if (prev === k) setSortDir(d => -d); else setSortDir(-1); return k; });
    setPage(1);
  };

  const toggleNotaire = (key) => {
    setNotaireFilter(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setPage(1);
  };

  /* ── Pipeline de filtrage ── */
  const statutFiltered = useMemo(
    () => applyStatutFilter(rows, statutSel),
    [rows, statutSel],
  );

  /* Chips notaire — calculés depuis statutFiltered pour que les compteurs restent cohérents */
  const notaireChips = useMemo(() => {
    const map = {};
    statutFiltered.forEach(r => {
      const key = r.notaireId ?? 'none';
      if (!map[key]) map[key] = { key, notaireId: r.notaireId, label: r.notaireNom || 'Sans notaire', count: 0 };
      map[key].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [statutFiltered]);

  const filtered = useMemo(() => {
    if (notaireFilter.size === 0) return statutFiltered;
    return statutFiltered.filter(r => notaireFilter.has(r.notaireId ?? 'none'));
  }, [statutFiltered, notaireFilter]);

  /* ── KPI agrégés ── */
  const kpi = useMemo(() => {
    const prixTotal = filtered.reduce((s, r) => s + r.prixNet, 0);
    const avance = filtered.reduce((s, r) => s + r.totalAvance, 0);
    const reliquat = filtered.reduce((s, r) => s + r.reliquat, 0);
    const avancePct = prixTotal > 0 ? Math.round((avance / prixTotal) * 100) : 0;
    return {
      nb: filtered.length,
      prixTotal,
      avance,
      reliquat,
      avancePct,
      nbChez: filtered.filter(r => getStatutKey(r) === 'CHEZ').length,
      nbEnCours: filtered.filter(r => getStatutKey(r) === 'EN_COURS').length,
      nbSigne: filtered.filter(r => getStatutKey(r) === 'SIGNE').length,
      nbNotYet: filtered.filter(r => getStatutKey(r) === 'NOT_YET').length,
    };
  }, [filtered]);

  /* ── Compteurs onglets ── */
  const tabCounts = useMemo(() => ({
    solde:     filtered.filter(r => r.totalAvance >= r.prixNet).length,
    partiel:   filtered.filter(r => r.totalAvance > 0 && r.totalAvance < r.prixNet).length,
    non_verse: filtered.filter(r => r.totalAvance === 0).length,
    reliquat:  filtered.filter(r => r.reliquat > 10).length,
  }), [filtered]);

  /* ── Filtrage onglet + tri + pagination ── */
  const tabFiltered = useMemo(() => applyTabFilter(filtered, activeTab), [filtered, activeTab]);

  const sortedRows = useMemo(() => [...tabFiltered].sort((a, b) => {
    const va = a[sortKey], vb = b[sortKey];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'string') return sortDir * va.localeCompare(vb, 'fr');
    return sortDir * (va - vb);
  }), [tabFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ── Vue résumé par projet — suit tous les filtres actifs (statut, notaire, onglet) ── */
  const resumeRows = useMemo(() => {
    const map = {};
    tabFiltered.forEach(r => {
      if (!map[r.projetId]) map[r.projetId] = { projetId: r.projetId, projetNom: r.projetNom, nb: 0, prixTotal: 0, avance: 0 };
      map[r.projetId].nb++;
      map[r.projetId].prixTotal += r.prixNet;
      map[r.projetId].avance += r.totalAvance;
    });
    return Object.values(map).map(r => ({ ...r, reliquat: r.prixTotal - r.avance })).sort((a, b) => b.nb - a.nb);
  }, [tabFiltered]);

  /* ── Export ── */
  const exportRows = () => exportExcel(
    sortedRows.map(r => ({
      'N° Dossier': r.numDossier,
      'Date Vente': fmtDate(r.dateReservation),
      'Type': r.typeVente,
      'Projet': r.projetNom,
      'Type bien': r.bienType,
      'Tranche': r.bienTranche,
      'Immeuble': r.bienImmeuble,
      'Étage': r.bienEtage,
      'N°': r.bienNumero,
      'Superficie m²': r.bienSuperficie,
      'Prix Net': r.prixNet,
      'Avance': r.totalAvance,
      'Reliquat': r.reliquat,
      'Statut notaire': STATUT_BADGE_CFG[getStatutKey(r)]?.label || '',
      'Date notaire': fmtDate(r.chezNotaireDate),
      'Client(s)': r.clientsNoms,
      'Tél(s)': r.clientsTels,
      'Commercial': r.commercial,
      'Chargé du bien': r.chargeBien,
      'Notaire': r.notaireNom,
    })),
    'notaire-prochainement',
  );

  const copyRows = () => {
    const { html, plain } = rowsToHtml(sortedRows.map(r => ({
      'Dossier': r.numDossier,
      'Client(s)': r.clientsNoms,
      'Projet': r.projetNom,
      'Prix Net': fmtUnit(r.prixNet, unit),
      'Avance': fmtUnit(r.totalAvance, unit),
      'Reliquat': fmtUnit(r.reliquat, unit),
      'Statut': STATUT_BADGE_CFG[getStatutKey(r)]?.label || '',
      'Notaire': r.notaireNom,
    })));
    copyHtmlToClipboard(html, plain);
  };

  /* ── Styles partagés ── */
  const thSt = {
    padding: '8px 10px', fontSize: '0.67rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.4, color: '#888',
    background: '#fdf8f4', borderBottom: '2px solid #d4c8bc', whiteSpace: 'nowrap',
  };
  const tdSt = {
    padding: '7px 10px', fontSize: '0.78rem', color: '#333',
    borderBottom: '1px solid #f5f0ea', verticalAlign: 'middle',
  };

  /* ── États vides ── */
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  if (!rows.length) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#aaa', fontSize: '0.85rem' }}>
      Aucun dossier en cours chez le notaire
    </div>
  );

  const COL_COUNT = multiProjet ? 17 : 16; // Contrat + Date + Type + [Projet] + TypeBien + Tranche + Sup + PrixNet + Avance + Reliquat + Statut + DateNotaire + Clients + Tels + Commercial + Chargé + Voir

  return (
    <div style={{ padding: 16 }}>

      {/* ══ FILTRE STATUT NOTAIRE ════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#7b3a9a', textTransform: 'uppercase', letterSpacing: 0.8, marginRight: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          🏛 Statut<InfoTip text="Filtre selon l'avancement du dossier chez le notaire. Signé client = chez_notaire=1 · Signature en cours = en_cours_signature=1 · Signé notaire = bien.STATUT = SIGNE NOTAIRE · Pas encore signé = aucune des conditions précédentes." />
        </span>
        {STATUT_NOTAIRE_OPTIONS.map(opt => {
          const active = statutSel.includes(opt.value);
          return (
            <button key={opt.value} onClick={() => toggleStatut(opt.value)}
              style={{
                padding: '4px 12px', borderRadius: 20, fontSize: '0.71rem', fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                border: `1.5px solid ${active ? '#7b3a9a' : '#d8cef0'}`,
                background: active ? '#7b3a9a' : '#f7f3ff',
                color: active ? '#fff' : '#7b3a9a',
              }}>
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* ══ CHIPS NOTAIRE ════════════════════════════════════════════════════════ */}
      {notaireChips.length > 1 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#9a7ab8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            🏛 Filtrer par notaire
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {notaireChips.map(chip => {
              const active = notaireFilter.has(chip.key);
              return (
                <button key={chip.key} onClick={() => toggleNotaire(chip.key)}
                  style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                    border: `1.5px solid ${active ? '#7b3a9a' : '#d8cef0'}`,
                    background: active ? '#7b3a9a' : '#f7f3ff',
                    color: active ? '#fff' : '#7b3a9a',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  {chip.label}
                  <span style={{
                    fontSize: '0.62rem', fontWeight: 700, borderRadius: 10, padding: '0 5px',
                    background: active ? 'rgba(255,255,255,0.25)' : '#e8ddf5',
                    color: active ? '#fff' : '#9a5ab8',
                  }}>
                    {chip.count}
                  </span>
                </button>
              );
            })}
            {notaireFilter.size > 0 && (
              <button onClick={() => { setNotaireFilter(new Set()); setPage(1); }}
                style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: 'none', border: '1px solid #ccc', color: '#999' }}>
                ✕ Tous
              </button>
            )}
          </div>
        </div>
      )}

      {/* ══ KPI CARDS ════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>

        {/* Card 1 — Dossiers */}
        <div style={{ flex: '1 1 160px', background: '#fff', border: '1.5px solid #b0c4de', borderRadius: 10, padding: '12px 16px', minWidth: 155, cursor: 'pointer' }}
          onClick={() => { setActiveTab(null); setPage(1); }}>
          <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4, display: 'flex', alignItems: 'center' }}>
            Dossiers<InfoTip text="Nombre de contrats actuellement en cours de traitement chez le notaire — snapshot en temps réel, indépendant de la période sélectionnée." />
          </div>
          <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#1b3a5f', lineHeight: 1 }}>{fmt(kpi.nb)}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {kpi.nbChez > 0 && (
              <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#e3f2fd', color: '#1565c0', borderRadius: 8, padding: '1px 7px' }}>
                Signé client {kpi.nbChez}
              </span>
            )}
            {kpi.nbEnCours > 0 && (
              <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#fff3e0', color: '#e65100', borderRadius: 8, padding: '1px 7px' }}>
                En cours {kpi.nbEnCours}
              </span>
            )}
            {kpi.nbSigne > 0 && (
              <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#e8f5e9', color: '#1b5e20', borderRadius: 8, padding: '1px 7px' }}>
                Signé {kpi.nbSigne}
              </span>
            )}
            {kpi.nbNotYet > 0 && (
              <span style={{ fontSize: '0.6rem', fontWeight: 700, background: '#f3e5f5', color: '#6a1b9a', borderRadius: 8, padding: '1px 7px' }}>
                Pas encore {kpi.nbNotYet}
              </span>
            )}
          </div>
        </div>

        {/* Card 2 — Valeur totale */}
        <div style={{ flex: '1 1 150px', background: '#fff', border: '1.5px solid #a5d6a7', borderRadius: 10, padding: '12px 16px', minWidth: 140 }}>
          <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4, display: 'flex', alignItems: 'center' }}>
            Valeur totale<InfoTip text="Somme des prix nets (contrat.PRIX_NET) de tous les dossiers affichés." />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1b5e20', lineHeight: 1 }}>{fmtUnit(kpi.prixTotal, unit)}</div>
          <div style={{ fontSize: '0.62rem', color: '#aaa', marginTop: 6 }}>Prix net des dossiers</div>
        </div>

        {/* Card 3 — Avance versée */}
        <div style={{ flex: '1 1 160px', background: '#fff', border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', minWidth: 150 }}>
          <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4, display: 'flex', alignItems: 'center' }}>
            Avance versée<InfoTip text="Total des encaissements reçus (contrat_encaissement.PART) sur les dossiers affichés, tous statuts confondus. Le % indique la part déjà encaissée du prix net total." />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: C.green, lineHeight: 1 }}>{fmtUnit(kpi.avance, unit)}</span>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: C.green }}>{kpi.avancePct}%</span>
          </div>
          {/* Barre de progression */}
          <div style={{ marginTop: 8, height: 5, background: '#e8f5e9', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${kpi.avancePct}%`, background: C.green, borderRadius: 4, transition: 'width 0.4s' }} />
          </div>
          <div style={{ fontSize: '0.6rem', color: '#aaa', marginTop: 4 }}>du prix net total</div>
        </div>

        {/* Card 4 — Reliquat */}
        <div style={{ flex: '1 1 150px', background: kpi.reliquat > 0 ? '#fff5f5' : '#fff', border: `1.5px solid ${kpi.reliquat > 0 ? '#ef9a9a' : '#e0d8d0'}`, borderRadius: 10, padding: '12px 16px', minWidth: 140 }}>
          <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4, display: 'flex', alignItems: 'center' }}>
            Reliquat<InfoTip text="Somme des reliquats : Prix net − Avance versée, par dossier. Représente le montant restant à encaisser avant ou après la signature notaire." />
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: kpi.reliquat > 0 ? '#c62828' : '#aaa', lineHeight: 1 }}>{fmtUnit(kpi.reliquat, unit)}</div>
          <div style={{ fontSize: '0.62rem', color: '#aaa', marginTop: 6 }}>Reste à encaisser</div>
          {/* Barre de progression inversée */}
          <div style={{ marginTop: 4, height: 5, background: '#ffebee', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${100 - kpi.avancePct}%`, background: '#e57373', borderRadius: 4, transition: 'width 0.4s' }} />
          </div>
        </div>

        {/* Card 5 — Soldés / Partiel (double) */}
        <div style={{ flex: '1 1 150px', background: '#fff', border: '1.5px solid #e0d8d0', borderRadius: 10, overflow: 'hidden', minWidth: 140 }}>
          <div onClick={() => { setActiveTab(prev => prev === 'solde' ? null : 'solde'); setPage(1); }}
            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0ebe4', background: activeTab === 'solde' ? '#e8f5e9' : 'transparent', transition: 'background 0.15s' }}>
            <div style={{ fontSize: '0.58rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Soldés</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#2e7d32' }}>{tabCounts.solde}</span>
              <span style={{ fontSize: '0.58rem', color: '#aaa' }}>avance ≥ prix</span>
            </div>
          </div>
          <div onClick={() => { setActiveTab(prev => prev === 'partiel' ? null : 'partiel'); setPage(1); }}
            style={{ padding: '10px 14px', cursor: 'pointer', background: activeTab === 'partiel' ? '#e3f2fd' : 'transparent', transition: 'background 0.15s' }}>
            <div style={{ fontSize: '0.58rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Partiel</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1565c0' }}>{tabCounts.partiel}</span>
              <span style={{ fontSize: '0.58rem', color: '#aaa' }}>en cours</span>
            </div>
          </div>
        </div>

      </div>

      {/* ══ TOOLBAR ══════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: '#888', fontWeight: 600 }}>
          {filtered.length} dossier{filtered.length !== 1 ? 's' : ''}
          {notaireFilter.size > 0 && <span style={{ color: '#7b3a9a', marginLeft: 4 }}>· {notaireFilter.size} notaire{notaireFilter.size > 1 ? 's' : ''}</span>}
        </span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', border: `1px solid ${C.border}` }}>
          {[{ key: 'detail', label: 'Détaillé' }, { key: 'resume', label: 'Résumé' }].map(({ key, label }) => (
            <button key={key} onClick={() => setViewMode(key)}
              style={{ padding: '4px 14px', fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: viewMode === key ? C.primary : '#fff', color: viewMode === key ? '#fff' : '#888' }}>
              {label}
            </button>
          ))}
        </div>
        <XBtn onClick={exportRows} color="#2e7d32" bg="#e8f5e9" border="#a5d6a7">↓ Excel</XBtn>
        <XBtn onClick={copyRows} color="#1565c0" bg="#e3f2fd" border="#90caf9">⎘ Copier</XBtn>
      </div>

      {/* ══ ONGLETS ══════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {TABS.map(t => {
          const active = activeTab === t.key;
          return (
            <button key={t.key}
              onClick={() => { setActiveTab(prev => prev === t.key ? null : t.key); setPage(1); }}
              style={{
                padding: '5px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
                cursor: 'pointer', border: `1.5px solid ${active ? t.color : '#e0d8d0'}`,
                background: active ? t.color : '#fff', color: active ? '#fff' : '#666',
                display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s',
              }}>
              {t.label}
              <InfoTip text={t.info} />
              <span style={{ fontSize: '0.63rem', fontWeight: 700, borderRadius: 10, padding: '0 5px', background: active ? 'rgba(255,255,255,0.25)' : '#f0ebe4', color: active ? '#fff' : '#888' }}>
                {tabCounts[t.key]}
              </span>
            </button>
          );
        })}
        {activeTab && (
          <button onClick={() => { setActiveTab(null); setPage(1); }}
            style={{ padding: '4px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: 'none', border: '1px solid #ccc', color: '#999' }}>
            ✕ Tous
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: '0.67rem', color: '#aaa' }}>
          {tabFiltered.length} résultat{tabFiltered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ══ VUE RÉSUMÉ ═══════════════════════════════════════════════════════════ */}
      {viewMode === 'resume' && (
        <div style={{ marginBottom: 16, borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thSt, borderRight: '2px solid #d4c8bc' }}>Projet</th>
                <th style={{ ...thSt, textAlign: 'right' }}>Nb dossiers</th>
                <th style={{ ...thSt, textAlign: 'right' }}>Prix total</th>
                <th style={{ ...thSt, textAlign: 'right' }}>Avance totale</th>
                <th style={{ ...thSt, textAlign: 'right', borderLeft: '2px solid #d4c8bc' }}>Reliquat total</th>
              </tr>
            </thead>
            <tbody>
              {resumeRows.length === 0 && (
                <tr><td colSpan={5} style={{ ...tdSt, textAlign: 'center', color: '#aaa', padding: 24 }}>Aucune donnée</td></tr>
              )}
              {resumeRows.map(r => (
                <tr key={r.projetId}
                  onMouseEnter={e => e.currentTarget.style.background = '#fdf8f4'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ ...tdSt, fontWeight: 700, color: C.primary, borderRight: '2px solid #d4c8bc' }}>{r.projetNom || '—'}</td>
                  <td style={{ ...tdSt, textAlign: 'right' }}>{r.nb}</td>
                  <td style={{ ...tdSt, textAlign: 'right', fontWeight: 600 }}>{fmtUnit(r.prixTotal, unit)}</td>
                  <td style={{ ...tdSt, textAlign: 'right', color: C.green, fontWeight: 600 }}>{fmtUnit(r.avance, unit)}</td>
                  <td style={{ ...tdSt, textAlign: 'right', color: r.reliquat > 0 ? '#c62828' : '#888', fontWeight: 600, borderLeft: '2px solid #d4c8bc' }}>{fmtUnit(r.reliquat, unit)}</td>
                </tr>
              ))}
            </tbody>
            {resumeRows.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f9fbf9', fontWeight: 700, borderTop: '2px solid #d4c8bc' }}>
                  <td style={{ ...tdSt, borderRight: '2px solid #d4c8bc' }}>Total</td>
                  <td style={{ ...tdSt, textAlign: 'right' }}>{resumeRows.reduce((s, r) => s + r.nb, 0)}</td>
                  <td style={{ ...tdSt, textAlign: 'right' }}>{fmtUnit(resumeRows.reduce((s, r) => s + r.prixTotal, 0), unit)}</td>
                  <td style={{ ...tdSt, textAlign: 'right', color: C.green }}>{fmtUnit(resumeRows.reduce((s, r) => s + r.avance, 0), unit)}</td>
                  <td style={{ ...tdSt, textAlign: 'right', color: '#c62828', borderLeft: '2px solid #d4c8bc' }}>{fmtUnit(resumeRows.reduce((s, r) => s + r.reliquat, 0), unit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
          </div>
        </div>
      )}

      {/* ══ VUE DÉTAILLÉE ════════════════════════════════════════════════════════ */}
      {viewMode === 'detail' && (
        <div>
          <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thSt}>Contrat</th>
                  <ThSort label="N° Dossier" field="numDossier" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} />
                  <ThSort label="Date Vente" field="dateReservation" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} />
                  <th style={thSt}>Type</th>
                  {multiProjet && <th style={thSt}>Projet</th>}
                  <ThSort label="Type bien" field="bienType" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} />
                  <th style={thSt}>Tranche / Imm / Ét / N°</th>
                  <th style={{ ...thSt, textAlign: 'right' }}>Sup. m²</th>
                  <ThSort label="Prix Net" field="prixNet" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} right />
                  <ThSort label="Avance" field="totalAvance" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} right />
                  <ThSort label="Reliquat" field="reliquat" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} right />
                  <th style={thSt}>Statut</th>
                  <th style={thSt}>Date notaire</th>
                  <th style={thSt}>Client(s)</th>
                  <th style={thSt}>Tél(s)</th>
                  <ThSort label="Commercial" field="commercial" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} />
                  <ThSort label="Chargé" field="chargeBien" sortKey={sortKey} asc={sortDir === 1} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {pagedRows.length === 0 && (
                  <tr>
                    <td colSpan={COL_COUNT} style={{ ...tdSt, textAlign: 'center', color: '#aaa', padding: 30 }}>
                      Aucune vente pour ce filtre
                    </td>
                  </tr>
                )}
                {pagedRows.map(r => {
                  const avancePct = r.prixNet > 0 ? Math.round((r.totalAvance / r.prixNet) * 100) : 0;
                  const isAsso = r.typeVente === 'ASSOCIATION';
                  return (
                    <tr key={r.venteId}
                      onMouseEnter={e => e.currentTarget.style.background = '#fdf8f4'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      {/* Colonne ID contrat */}
                      <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>
                        <a href={`https://shems.ngrok.io/consultation/${r.venteId}`} target="_blank" rel="noreferrer"
                          style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>
                          #{r.venteId}
                        </a>
                      </td>
                      {/* N° Dossier */}
                      <td style={{ ...tdSt, fontWeight: 700, color: C.primary }}>{r.numDossier || '—'}</td>
                      <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{fmtDate(r.dateReservation)}</td>
                      <td style={tdSt}>
                        <span style={{ fontSize: '0.63rem', fontWeight: 700, borderRadius: 8, padding: '2px 7px', background: isAsso ? '#f3e5f5' : '#e8f5e9', color: isAsso ? '#6a1b9a' : '#1b5e20' }}>
                          {isAsso ? 'Asso.' : 'Normal'}
                        </span>
                      </td>
                      {multiProjet && (
                        <td style={{ ...tdSt, color: C.primary, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.projetNom}</td>
                      )}
                      <td style={tdSt}>{r.bienType}</td>
                      <td style={{ ...tdSt, fontSize: '0.72rem', color: '#666', whiteSpace: 'nowrap' }}>
                        {[r.bienTranche, r.bienImmeuble, r.bienEtage, r.bienNumero].filter(Boolean).join(' / ') || '—'}
                      </td>
                      <td style={{ ...tdSt, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.bienSuperficie != null ? r.bienSuperficie.toLocaleString('fr-FR') : '—'}
                      </td>
                      <td style={{ ...tdSt, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtUnit(r.prixNet, unit)}</td>
                      <td style={{ ...tdSt, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ color: C.green, fontWeight: 600 }}>{fmtUnit(r.totalAvance, unit)}</span>
                        {r.prixNet > 0 && <span style={{ fontSize: '0.6rem', color: '#aaa', marginLeft: 4 }}>{avancePct}%</span>}
                      </td>
                      <td style={{ ...tdSt, textAlign: 'right', fontWeight: r.reliquat > 10 ? 700 : 400, color: r.reliquat > 10 ? '#c62828' : '#aaa', whiteSpace: 'nowrap' }}>
                        {fmtUnit(r.reliquat, unit)}
                      </td>
                      <td style={tdSt}><StatutBadge row={r} /></td>
                      <td style={{ ...tdSt, whiteSpace: 'nowrap', fontSize: '0.72rem' }}>{fmtDate(r.chezNotaireDate)}</td>
                      <td style={{ ...tdSt, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.clientsNoms || '—'}</td>
                      <td style={tdSt}><TelCell tels={r.clientsTels} /></td>
                      <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{r.commercial || '—'}</td>
                      <td style={{ ...tdSt, whiteSpace: 'nowrap' }}>{r.chargeBien || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              {sortedRows.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#fdf8f4', fontWeight: 700, borderTop: '2px solid #e8ddd4' }}>
                    <td colSpan={multiProjet ? 7 : 6} style={{ ...tdSt, fontSize: '0.7rem', color: '#888' }}>
                      Total — {sortedRows.length} dossier{sortedRows.length !== 1 ? 's' : ''}
                    </td>
                    <td style={{ ...tdSt, textAlign: 'right' }}>{fmtUnit(sortedRows.reduce((s, r) => s + r.prixNet, 0), unit)}</td>
                    <td style={{ ...tdSt, textAlign: 'right', color: C.green }}>{fmtUnit(sortedRows.reduce((s, r) => s + r.totalAvance, 0), unit)}</td>
                    <td style={{ ...tdSt, textAlign: 'right', color: '#c62828' }}>{fmtUnit(sortedRows.reduce((s, r) => s + r.reliquat, 0), unit)}</td>
                    <td colSpan={7} />
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
          <DetailPager page={page} setPage={setPage} totalPages={totalPages} total={sortedRows.length} PAGE_SIZE={PAGE_SIZE} />
        </div>
      )}

    </div>
  );
};

export default NotaireSoonSection;
